/**
 * Blossom — tabs.js
 * <tabs-component> — accessible tabs (WAI-ARIA "tabs with automatic activation").
 * Used by: Featured products (collection tabs), Sustainability (commitments),
 * Press quotes (logo tabs).
 *
 * Markup:
 *   <tabs-component [data-hover="true"] [data-autoplay="6"]>
 *     <div role="tablist" aria-label="…">
 *       <button role="tab" id="Tab-1" aria-controls="Panel-1" aria-selected="true" tabindex="0">…</button>
 *       <button role="tab" id="Tab-2" aria-controls="Panel-2" aria-selected="false" tabindex="-1">…</button>
 *     </div>
 *     <div role="tabpanel" id="Panel-1" aria-labelledby="Tab-1" tabindex="0">…</div>
 *     <div role="tabpanel" id="Panel-2" aria-labelledby="Tab-2" tabindex="0" hidden>…</div>
 *     <button data-tabs-autoplay aria-pressed="false">…</button>   (optional pause toggle)
 *     <a data-tabs-link href="…">…</a>   (optional; href follows the active tab's data-link-url)
 *   </tabs-component>
 *
 * Keyboard: Arrow keys move between tabs (Up/Down when aria-orientation="vertical"),
 * Home/End jump to first/last. Emits `tabs:change` { index, tab, panel }.
 */
(() => {
  if (customElements.get('tabs-component')) return;

  class TabsComponent extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.tablist = this.querySelector('[role="tablist"]');
      if (!this.tablist) return;
      this.initialized = true;

      this.tablist.addEventListener('click', (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (!tab || !this.owns(tab)) return;
        event.preventDefault();
        this.stopAutoplay(true);
        this.activate(tab);
      });

      this.tablist.addEventListener('keydown', (event) => this.onKeydown(event));

      if (this.dataset.hover === 'true') {
        this.tablist.addEventListener('pointerover', (event) => {
          if (event.pointerType !== 'mouse') return;
          const tab = event.target.closest('[role="tab"]');
          if (tab && this.owns(tab) && tab.getAttribute('aria-selected') !== 'true') this.activate(tab);
        });
      }

      this.setupAutoplay();
      this.syncLink(this.selectedTab);

      if (window.Shopify && window.Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          const tab = this.tabForElement(event.target);
          if (!tab) return;
          this.stopAutoplay(true);
          this.activate(tab);
        });
      }
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    get tabs() {
      return Array.from(this.tablist.querySelectorAll('[role="tab"]')).filter((tab) => this.owns(tab));
    }

    get selectedTab() {
      return this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    }

    owns(element) {
      return element.closest('tabs-component') === this;
    }

    panelFor(tab) {
      const id = tab && tab.getAttribute('aria-controls');
      return id ? document.getElementById(id) : null;
    }

    /** Find the tab matching a block element selected in the theme editor. */
    tabForElement(element) {
      const tab = element.closest('[role="tab"]');
      if (tab && this.owns(tab)) return tab;
      const panel = element.closest('[role="tabpanel"]');
      if (panel) return this.tabs.find((candidate) => candidate.getAttribute('aria-controls') === panel.id) || null;
      const blockId = element.dataset.blockId;
      if (blockId) return this.tabs.find((candidate) => candidate.dataset.blockId === blockId) || null;
      return null;
    }

    activate(tab, { focus = false } = {}) {
      if (!tab) return;
      this.tabs.forEach((candidate) => {
        const selected = candidate === tab;
        candidate.setAttribute('aria-selected', String(selected));
        candidate.setAttribute('tabindex', selected ? '0' : '-1');
        candidate.classList.toggle('is-active', selected);
        const panel = this.panelFor(candidate);
        if (panel) {
          panel.hidden = !selected;
          panel.classList.toggle('is-active', selected);
        }
      });
      if (focus) tab.focus();
      this.syncLink(tab);
      const index = this.tabs.indexOf(tab);
      this.dispatchEvent(
        new CustomEvent('tabs:change', { detail: { index, tab, panel: this.panelFor(tab) }, bubbles: true })
      );
    }

    syncLink(tab) {
      const link = this.querySelector('[data-tabs-link]');
      if (!link || !tab) return;
      if (tab.dataset.linkUrl) {
        link.href = tab.dataset.linkUrl;
        link.removeAttribute('aria-disabled');
        link.hidden = false;
      } else {
        link.hidden = true;
      }
    }

    onKeydown(event) {
      const tabs = this.tabs;
      const current = tabs.indexOf(event.target.closest('[role="tab"]'));
      if (current < 0) return;
      const vertical = this.tablist.getAttribute('aria-orientation') === 'vertical';
      const next = vertical ? 'ArrowDown' : 'ArrowRight';
      const prev = vertical ? 'ArrowUp' : 'ArrowLeft';
      let target = null;
      if (event.key === next) target = tabs[(current + 1) % tabs.length];
      else if (event.key === prev) target = tabs[(current - 1 + tabs.length) % tabs.length];
      else if (event.key === 'Home') target = tabs[0];
      else if (event.key === 'End') target = tabs[tabs.length - 1];
      if (!target) return;
      event.preventDefault();
      this.stopAutoplay(true);
      this.activate(target, { focus: true });
      target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    /* ------------------------------------------------------- Autoplay */
    setupAutoplay() {
      this.autoplaySeconds = Number(this.dataset.autoplay || 0);
      this.toggleButton = this.querySelector('[data-tabs-autoplay]');
      const reduced =
        document.documentElement.dataset.reducedMotion === 'true' ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!this.autoplaySeconds || this.tabs.length < 2 || reduced) {
        if (this.toggleButton) this.toggleButton.hidden = true;
        return;
      }
      this.playing = true;
      this.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.addEventListener('mouseleave', () => this.resumeAutoplay());
      this.addEventListener('focusin', () => this.pauseAutoplay());
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.resumeAutoplay();
      });
      this.toggleButton?.addEventListener('click', () => {
        if (this.playing) this.stopAutoplay(true);
        else {
          this.playing = true;
          this.updateToggle();
          this.resumeAutoplay();
        }
      });
      this.updateToggle();
      this.resumeAutoplay();
    }

    updateToggle() {
      if (!this.toggleButton) return;
      this.toggleButton.setAttribute('aria-pressed', String(!this.playing));
      const label = this.playing ? this.toggleButton.dataset.labelPause : this.toggleButton.dataset.labelPlay;
      if (label) this.toggleButton.setAttribute('aria-label', label);
    }

    pauseAutoplay() {
      clearInterval(this.timer);
      this.timer = null;
    }

    resumeAutoplay() {
      if (!this.playing || this.timer) return;
      this.timer = setInterval(() => {
        const tabs = this.tabs;
        const index = tabs.indexOf(this.selectedTab);
        this.activate(tabs[(index + 1) % tabs.length]);
      }, this.autoplaySeconds * 1000);
    }

    stopAutoplay(hard = false) {
      this.pauseAutoplay();
      if (hard && this.playing) {
        this.playing = false;
        this.updateToggle();
      }
    }
  }

  customElements.define('tabs-component', TabsComponent);
})();
